import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { createSelector } from 'reselect';
import moment from 'moment';

import { Urls, ModalTypes, ServiceTypes, ReportTypes } from 'constants/constants';
import { formatHours as fm, formatNumber as fn, formatPercent as fp, formatHours } from 'utils/utils';
import { usePolling } from 'utils/components';

import Stream, { selectStream, isStreamLive, selectStreamThumbs } from 'store/models/Stream';
import { selectChannel } from 'store/models/Channel';
import { selectSessionByStreamId } from 'store/models/Session';
import { selectScenesByIds } from 'store/models/Scene';
import { selectGamesByIds } from 'store/models/Game';
import { selectLinksByIds } from 'store/models/Link';
import Stats, { selectStreamStats, selectStreamGameStats, selectStreamSceneStats, selectStreamLinkStats } from 'store/models/Stats';

import { ContentWide, MegaHeader, Header, Section, SectionTitle } from 'components/Content';
import ScrollToTop from 'components/ScrollToTop';
import ChannelCard from 'components/ChannelCard';
import { StatBox, StatsBoxList } from 'components/Stats';

import { SceneStatList } from 'components/SceneItem';
import { Button, TextButton } from 'components/Button';
import { GameList } from 'components/GameItem';
import { LinkList } from 'components/LinkItem';
import { ThumbList } from 'components/ThumbItem';
import { EmptyItem } from 'components/EmptyItem';
import * as Icons from 'components/Svg';
import { TagList, ChannelGroupTag } from 'components/Tag';
import { LoadingText } from 'components/Loading';
import UIState from 'store/UIState';





// ======================
// STORE
// ======================
const selectors = createSelector(
  (state, streamId) => selectStream(state, streamId),
  (state, streamId) => selectStreamThumbs(state, streamId),
  (state, streamId) => selectStream(state, streamId) |> #?.channelId |> selectChannel(state, #),
  (state, streamId) => selectSessionByStreamId(state, streamId),
  (state, streamId) => selectStreamStats(state, streamId),
  (state, streamId) => selectStream(state, streamId) |> _.map(#?.games, 'gameId') |> selectGamesByIds(state, #),
  (state, streamId) => selectStreamGameStats(state, streamId),
  (state, streamId) => selectStreamSceneStats(state, streamId) |> _.keys(#) |> selectScenesByIds(state, #),
  (state, streamId) => selectStreamSceneStats(state, streamId),
  (state, streamId) => selectStreamLinkStats(state, streamId) |> _.keys(#) |> selectLinksByIds(state, #),
  (state, streamId) => selectStreamLinkStats(state, streamId),
  (stream, liveThumbUrls, channel, session, stats, games, gameStats, scenes, sceneStats, links, linkStats) => {
    return { 
      stream,
      liveThumbUrls,
      channel,
      session,
      stats,
      games,
      gameStats,
      scenes, 
      sceneStats, 
      links, 
      linkStats,
    }
  },
);


// ======================
// COMPONENT
// ====================== 
const StreamDetailsScreen = ({match}) => {
  const { streamId } = match.params;
  const { stream, channel, liveThumbUrls, stats, games, gameStats, scenes, sceneStats, links, linkStats } = useSelector(s => selectors(s, streamId));
  const [loading, setLoading] = useState({
    stream: false,
    streamStats: false,
    gameStats: false,
    sceneStats: false,
    linkStats: false,
  });
  const [showAll, setShowAll] = useState(false);
  const dispatch = useDispatch();

  const fetchData = async () => {
    let isLoading = _.mapValues(loading, () => true);
    setLoading(isLoading);

    await dispatch(Stream.actions.fetchStreamDetails(streamId));
    setLoading({..._.set(isLoading, 'stream', false)});

    await dispatch(Stats.actions.fetchStreamStats(streamId));
    setLoading({..._.set(isLoading, 'streamStats', false)});

    await dispatch(Stats.actions.fetchStreamGameStats(streamId));
    setLoading({..._.set(isLoading, 'gameStats', false)});

    await dispatch(Stats.actions.fetchStreamSceneStats(streamId));
    setLoading({..._.set(isLoading, 'sceneStats', false)});

    await dispatch(Stats.actions.fetchStreamLinkStats(streamId));
    setLoading({..._.set(isLoading, 'linkStats', false)});
  }

  useEffect(() => {
    (async () => {
      await fetchData();
    })();
  }, []);

  const serviceName = channel?.service === ServiceTypes.TWITCH ? 'Twitch' : 'YouTube';
  const serviceIcon = channel?.service === ServiceTypes.TWITCH ? <Icons.TwitchIcon /> : <Icons.YouTubeIcon />;
  const isLive = isStreamLive(stream);
  const start = moment(stream?.start);
  const end = moment(stream?.end ?? start);
  const hours = end.diff(start ?? end, 'minutes') |> #/60 |> _.round(#, 2);
  const title = stream?.titles?.[0];
  const date = start?.format("MMM DD, YYYY");
  const startTime = start?.format("h:mm a");
  const endTime = end?.format("h:mm a");
  const mediaValue = fn(stats?.totalMediaValue);
  const thumbSample = [];
  const liveThumbs = liveThumbUrls;

  if(showAll && liveThumbs && liveThumbs.length > 0) {
    thumbSample.push(...liveThumbs);
  } else if(liveThumbs && liveThumbs.length > 0) {
    const firstIndex = 0;
    const lastIndex = liveThumbs.length - 1;
    const numItems = 8; // Including the first and last index, and 6 equidistant items in between
    const step = Math.floor((lastIndex - firstIndex) / (numItems - 1));

    thumbSample.push(liveThumbs[firstIndex]);
    for (let i = 1; i < numItems - 1; i++) {
      const index = firstIndex + i * step;
      thumbSample.push(liveThumbs[index]);
    }
    thumbSample.push(liveThumbs[lastIndex]);
  }

  const onThumbClick = ({imageUrl}) => dispatch(UIState.actions.showModal(ModalTypes.IMAGE, { liveThumbs: liveThumbUrls, index: liveThumbUrls?.findIndex(t => t.imageUrl === imageUrl) }));
  const onToggleShowAll = () => setShowAll(!showAll);

  return (<>
    <ScrollToTop />
    <MegaHeader className="stream-details-head">
      <div className="stream-head">
        <ChannelCard channel={channel} showName={false} size="xl" />
        <div className="stream-meta">
          <div className='channel-name'>{ channel?.serviceName }</div>
          <div className='stream-title'>{ title }</div>
          
          <div className="stream-date">
            <span className="date">{ date }</span>
          </div>
          <div className="stream-time">
            <span>{ startTime }</span>
            <Icons.RightChevronIcon size="s" />
            { isLive
            ? <span className="time-live"><Icons.SignalIcon size="s" color="green" /> LIVE</span>
            : <span>{ endTime }</span>}
          </div>
        </div>
      </div>
    </MegaHeader>
    
    <ContentWide className="stream-details">
      { !_.isEmpty(thumbSample) && 
      <Section size="l">
        <SectionTitle className="split">
          Live Screenshots
          <TextButton onClick={onToggleShowAll} className="show-all-button">
            { showAll ? 'Show Less' : 'Show All' } 
          </TextButton>
        </SectionTitle>
        <ThumbList liveThumbs={thumbSample} onClick={onThumbClick} />
        { showAll && <Button onClick={onToggleShowAll} className="light show-less">Show Less</Button>}
      </Section>}
      
      <Section size="l">
        <SectionTitle>Games</SectionTitle>
        <GameList games={games} stats={gameStats} loading={loading.gameStats} loadingCount={2} showName={false} />
      </Section>
      
      <Section size="l">
        <SectionTitle>Slides</SectionTitle>
        <StatsBoxList rowCount={4} wrap={true}>
          <StatBox
            label="Slideshow Value"
            value={`$${fn(stats?.sceneMediaValue)}`}
            loading={loading.streamStats} />
          <StatBox
            label="Impressions"
            value={fn(stats?.sceneViews)}
            loading={loading.streamStats} />
          <StatBox
            label="Fill Rate"
            value={fp(stats?.fillRate)}
            loading={loading.streamStats} />
          <StatBox
            label="Unique Slides"
            value={fn(stats?.sceneIds)}
            loading={loading.streamStats} />
        </StatsBoxList>
        <SceneStatList scenes={scenes} sceneStats={sceneStats} loading={loading.sceneStats} loadingCount={2} />
      </Section>

      { channel?.service !== ServiceTypes.YOUTUBE && 
      <Section size="l">
        <SectionTitle>Links & Chat</SectionTitle>
        <StatsBoxList rowCount={4} wrap={true}>
          <StatBox
            label="Click Value"
            value={`$${fn(stats?.clicksMediaValue)}`}
            loading={loading.streamStats} />
          <StatBox
            label="Clicks"
            value={fn(stats?.clicks)}
            loading={loading.streamStats} />
          <StatBox
            label="Link Views"
            value={fn(stats?.messageViews)}
            loading={loading.streamStats} />
          <StatBox
            label="CTR"
            value={fp(stats?.linkCtr, 1)}
            loading={loading.streamStats} />
        </StatsBoxList>
        <LinkList links={links} linkStats={linkStats} activeLinks={null} loading={loading.linkStats} loadingCount={2} />
      </Section>}
    </ContentWide>
  </>)
}
export default StreamDetailsScreen;
